import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  BaseAutocomplete,
  BaseAutocompleteProps,
} from '../autocomplete/BaseAutocomplete/BaseAutocomplete';
import { InputAddons } from '../input-addons/InputAddons';
import {
  IAddonsProps,
  IInputSearchProps,
  InputValueMode,
} from '../common/props';
import { ValueInputDetail } from '../value-input-detail/ValueInputDetail';
import { isEmpty } from 'lodash-es';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { Button } from '../button/Button';
import { Icon } from '../icon/Icon';
import { mcHistory } from '@mc/react-ui-icons';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';

export type SelectBoxProps<API_TYPE, VAL_TYPE> = IAddonsProps &
  IInputSearchProps & {
    latestItems?: API_TYPE[];
    valueMode?: InputValueMode;
    detailExtra?: (
      value: VAL_TYPE,
      onChange: ((val?: VAL_TYPE | undefined) => void) | undefined,
      disabled?: boolean
    ) => React.ReactNode;
  } & Omit<
    BaseAutocompleteProps<API_TYPE, VAL_TYPE>,
    'touched' | 'error' | 'inline' | 'getDropDownIcon' | 'actions'
  >;

export class SelectBox<
  API_TYPE,
  VAL_TYPE = API_TYPE,
> extends React.PureComponent<SelectBoxProps<API_TYPE, VAL_TYPE>> {
  render() {
    const {
      className,
      tree,
      inputProps,
      addonAfter,
      addonAfterCompact,
      latestItems = [],
      onSearch,
      disabled,
      readOnly,
      showMenuOnFocus = false,
      colors = false,
      valueMode,
      renderItemContent,
      getItemName,
      detailExtra,
      getItemRenderClass,
      dataTest,
      ...other
    } = this.props;
    let cls = classNames(
      className,
      Classes.BASE_AUTOCOMPLETE,
      Classes.SELECTBOX,
      {
        [Classes.BASE_AUTOCOMPLETE_TREE]: tree,
        [Classes.FIELD_INPUT_DISABLED]: disabled,
        [Classes.FIELD_INPUT_READONLY]: readOnly,
      }
    );

    return other?.value && valueMode === 'detail' ? (
      <ValueInputDetail<API_TYPE, VAL_TYPE>
        value={other.value}
        onChange={other.onChange}
        renderItemContent={renderItemContent}
        getItemName={getItemName}
        renderExtra={detailExtra}
        disabled={disabled}
        readOnly={readOnly}
      />
    ) : (
      <InputAddons
        addonAfter={
          isEmpty(latestItems) ? (
            addonAfter
          ) : (
            <ButtonToolbar>
              <Button
                icon={<Icon icon={mcHistory} />}
                disabled={disabled || readOnly}
                menu={
                  <List
                    mode={'input'}
                    className={Classes.AUTOCOMPLETE_LATEST_ITEMS}
                  >
                    {latestItems?.map((item, id) => {
                      // @ts-ignore
                      const name = getItemName ? getItemName(item) : item?.name;
                      return (
                        <ListItem
                          key={id}
                          // @ts-ignore
                          onClick={() => other.onChange?.(item)}
                        >
                          {renderItemContent ? (
                            renderItemContent(item, name)
                          ) : (
                            <span className="item-text">{name}</span>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                }
                popupProps={{ position: 'bottom right' }}
              />
              {addonAfter}
            </ButtonToolbar>
          )
        }
        addonAfterCompact={addonAfterCompact}
      >
        <BaseAutocomplete
          dataTest={dataTest}
          showMenuOnFocus={showMenuOnFocus}
          className={cls}
          inputProps={{ className: Classes.FIELD_INPUT, ...inputProps }}
          tree={tree}
          onSearch={onSearch}
          {...other}
          renderItemContent={renderItemContent}
          getItemRenderClass={(item) => {
            const defaultItemContentClass =
              valueMode === 'detail' && !!renderItemContent
                ? 'item-content-padding'
                : '';
            return getItemRenderClass
              ? getItemRenderClass(item)
              : defaultItemContentClass;
          }}
          getItemName={getItemName}
          disabled={disabled}
          readOnly={readOnly}
          colors={colors}
        />
      </InputAddons>
    );
  }
}
